import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { QESButton } from "@app/shared/qes-standards";
import { DropdownItem, MultiselectDropdown, MultiselectNestedDropdown } from "@app/shared/checkbox-dropdown";
import { AnalysisSpecialtyGroup } from "@app/projects/results/common";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { CountyProjectContext,  } from "@app/projects";
import ProviderHubGapView from "./ProviderHubGapsView";
import { ProviderHubContext, ProviderHubContextProvider } from "./contexts/ProviderHubContext";
import ProviderHubProvidersContainer from "./ProviderHubProvidersContainer";
import ProviderHubService from "./providerHubService";
import { LoadingSpinner } from "@app/shared";
import { FlyoutLink } from "@app/shared/Flyouts";
import AIChatPanel from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPanel";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import ChatbotService from "@app/projects/network-assessment/provider-hub/ai-assistant/chatbotService";

const WrappedProviderHubLandingPage: FC = (): JSX.Element => {

  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const providerHubService = useRef(new ProviderHubService()).current;
  const chatbotService = new ChatbotService();

  const [loading, setLoading] = useState<boolean>(false);

  const convertSpecialtiesToDropdownItems = (data: AnalysisSpecialtyGroup[]): DropdownItem<string>[] => {
    return data.map(item => ({
      key: item.code,
      name: item.name,
    }));
  };

  const getSelectionText = (currentSelections: unknown[], totalOptions: unknown[]) => {
    const current = currentSelections.length;
    const total = totalOptions.length;
    if (total === 0) {
      return "Loading";
    }
    return `${current.toLocaleString()} of ${total.toLocaleString()} item(s) selected`;
  }

  // Specialties dropdown data
  const sortedSpecialtyGroups = projectContext.runConfiguration.specialtyGroups.sort((a, b) => a.name.localeCompare(b.name));
  const specialties = convertSpecialtiesToDropdownItems(sortedSpecialtyGroups);

  const specialtySelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.selectedSpecialties, specialties);
  }, [providerHubContext.selectedSpecialties]);

  // Service Areas dropdown data
  const serviceAreaOptions: NestingParentDropdownItem[] = [];
  projectContext.runConfiguration.serviceArea.subRegions.map((state) => {
    const children: DropdownItem<string>[] = state.subRegions.map(county => {
      return { key: county.regionCode, name: county.displayName }
    });
    const nestedServiceArea: NestingParentDropdownItem = {
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children,
      key: state.regionCode,
      name: state.displayName
    };
    serviceAreaOptions.push(nestedServiceArea);
  });

  const serviceAreaSelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.selectedServiceAreas, serviceAreaOptions.flatMap((sa) => sa.children));
  }, [providerHubContext.selectedServiceAreas]);

  const isSpecialtiesDropdownEnabled = useMemo(() => {
    return providerHubContext.selectedServiceAreas.length > 0;
  }, [providerHubContext.selectedServiceAreas]);

  const isStartButtonEnabled = useMemo(() => {
    return providerHubContext.selectedServiceAreas.length > 0 && providerHubContext.selectedSpecialties.length > 0 ;
  }, [providerHubContext.selectedServiceAreas, providerHubContext.selectedSpecialties]);

  const getRegionSetSpecialtyGroupsWithGapsData = async () => {
    const gapsData = await providerHubService.getRegionSetSpecialtyGroupsWithGaps();
    providerHubContext.setRegionSetSpecialtyGroupsWithGaps(gapsData);
  }

  const getProviderHubServicingProvidersData = async () => {
    const providersData = await providerHubService.getProviderHubServicingProviders(providerHubContext.pageIndex, providerHubContext.pageSize, providerHubContext.providersFilters);
    providerHubContext.setProviderHubServicingProviders(providersData);
    providerHubContext.setTriggerProvidersRefreshData(false);
  }


  const handleStartClick = async () => {

    setLoading(true);

    getRegionSetSpecialtyGroupsWithGapsData();
    getProviderHubServicingProvidersData();

    providerHubContext.setDisplayGapsAndProvidersTable(true);
    setLoading(false);

  }

  useEffect(() => {

    if (providerHubContext.triggerProvidersRefreshData){
      getProviderHubServicingProvidersData();
    }

  }, [providerHubContext.triggerProvidersRefreshData]);

  return (

    <div className="provider-hub_landing-page-main-container">

      {featureFlagContext.enabledFeatureFlags.has(Features.SNOWFLAKE_AI_POC) && (
        <FlyoutLink
          content={{
            body: <AIChatPanel sendMessages={messages => chatbotService.sendConversation(messages)} startMessage="What questions can I answer about your Provider Hub data?"/>,
            title: 'Chat with AI Analyst',
          }}
          testId="provider-hub-landing-page__ai-chat-flyout-link"
          size="40"
          classNames="qes-button-secondary p-0 pl-2">
          <p>Chat with AI Analyst</p>
        </FlyoutLink>
      )}

      <div className="provider-hub_landing-page-top-actions-container">

        <MultiselectNestedDropdown
          customSelectInputText={serviceAreaSelectionText}
          items={serviceAreaOptions}
          label="What are the service areas?"
          onSelectionChanged={(items) => providerHubContext.setSelectedServiceAreas(items)}
          selectedItems={providerHubContext.selectedServiceAreas}
          testId="provider-hub__service-areas-multi-select-dropdown"
        />

        <MultiselectDropdown
          customWidth={true}
          customSelectInputText={specialtySelectionText}
          disabled={!isSpecialtiesDropdownEnabled}
          items={specialties}
          label="What are the specialty types?"
          onSelectionChanged={(selectedItems) => providerHubContext.setSelectedSpecialties(selectedItems)}
          selectedItems={providerHubContext.selectedSpecialties}
          testId="provider-hub__specialties-multi-select-dropdown"
          showTooltipOnOverflow={true}
        />

        <div className="provider-hub__start-button">
          <QESButton
            disabled={!isStartButtonEnabled}
            qesType="primary"
            qesTestId="provider-hub__start-button"
            onClick={handleStartClick}>
            Start
          </QESButton>
        </div>

      </div>

      {loading &&
        <div className="row loading-spinner-container">
          <div className="col-12">
            <div className="text-center">
              <LoadingSpinner/>
            </div>
          </div>
        </div>
      }

      {providerHubContext.displayGapsAndProvidersTable &&

        <div className="provider-hub__table-container">
          <div className="provider-hub__table-container-gaps-view">
            <ProviderHubGapView/>
          </div>
          <div className="provider-hub__table-container-providers-view">
            <ProviderHubProvidersContainer/>
          </div>
        </div>

      }

    </div>
  )
}

const ProviderHubLandingPage: FC = () => (
  <ProviderHubContextProvider>
    <WrappedProviderHubLandingPage/>
  </ProviderHubContextProvider>
);

export default ProviderHubLandingPage;
